import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@sea-snag/ui';
import { useMemo, useState } from 'react';

import { useCanSeeAdmin } from '@/hooks/use-can-see-admin';

import { useIncomeOverview, useSetBoatIncomeSettings } from '../../hooks/use-income';
import {
  filterBoats,
  type IncomeFilters,
  type IncomeMetric,
  monthlySeries,
  NO_FILTERS,
  totalsOf,
} from '../../lib/income';
import { IncomeFiltersBar } from './income-filters';
import { IncomeMonthlyChart } from './income-monthly-chart';
import { SummaryCards } from './income-summary-cards';
import { IncomeTaxSetting } from './income-tax-setting';
import { PerBoatIncomeTable } from './per-boat-income-table';

/**
 * Income → Overview (#217): the season's secured charter value, future
 * confirmed charters included. Not cash received, and not profit.
 */
export function IncomeOverview({ orgPid, year }: { orgPid: string; year: number }) {
  const canEdit = useCanSeeAdmin();
  const { data, isLoading } = useIncomeOverview(orgPid, year);
  const saveSettings = useSetBoatIncomeSettings(orgPid);
  const [filters, setFilters] = useState<IncomeFilters>(NO_FILTERS);
  const [metric, setMetric] = useState<IncomeMetric>('net');
  const [picked, setPicked] = useState<string | null>(null);

  const currency = data?.currencies.includes(picked ?? '')
    ? picked!
    : (data?.currencies[0] ?? 'EUR');
  const boats = useMemo(() => (data ? filterBoats(data.boats, filters) : []), [data, filters]);
  const totals = useMemo(() => totalsOf(boats, currency), [boats, currency]);

  if (isLoading || !data) return <Skeleton className="h-[600px] w-full" />;

  return (
    <div className="space-y-6">
      <IncomeTaxSetting
        orgPid={orgPid}
        year={year}
        tax={data.tax}
        currency={currency}
        canEdit={canEdit}
      />

      <IncomeFiltersBar
        boats={data.boats}
        filters={filters}
        onChange={setFilters}
        included={boats.length}
      />

      {data.currencies.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>Priced in</span>
          {data.currencies.map((c) => (
            <Button
              key={c}
              variant={c === currency ? 'default' : 'outline'}
              size="sm"
              className="h-7 px-2"
              aria-pressed={c === currency}
              onClick={() => setPicked(c)}
            >
              {c}
            </Button>
          ))}
          <span>Currencies are never added together.</span>
        </div>
      )}

      {boats.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-sm text-muted-foreground">
            {data.boats.length === 0
              ? `No boats in the fleet for ${year}.`
              : 'No boats match these filters.'}
            {data.boats.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => setFilters(NO_FILTERS)}>
                Reset filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <SummaryCards totals={totals} currency={currency} taxRatePct={data.tax.ratePct} />

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">By month charters ended</CardTitle>
            </CardHeader>
            <CardContent>
              <IncomeMonthlyChart
                series={monthlySeries(boats, currency, year, metric)}
                metric={metric}
                onMetricChange={setMetric}
                currency={currency}
              />
            </CardContent>
          </Card>

          <PerBoatIncomeTable
            boats={boats}
            currency={currency}
            year={year}
            totals={totals}
            canEdit={canEdit}
            isPending={saveSettings.isPending}
            onSaveSettings={(boatPid, settings) =>
              saveSettings.mutateAsync({ boatPid, data: settings })
            }
          />
        </>
      )}
    </div>
  );
}
